
import { useState, useRef, useEffect } from "react";
import Hls from "hls.js";
import AudioPlayer, { RHAP_UI } from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";
import { COOKIE, getCookie } from "../../../services/cookie";
import { getColorClass } from "../../../functions/functions";
import { Spin } from "antd";
import constants from "../../../constants/constants";

const AudioComponent = ({
	chapter_id,
	isPlayed,
	chapterDetails = null,
	isPlaying = false,
	setIsPlaying = () => { },
	setAudioLoading = () => { },
}) => {
	const audioRef = useRef(null);
	const [isPodcastPlayed, setIsPodcastPlayed] = useState(isPlayed);
	const [isLoading, setIsLoading] = useState(true);
	const hlsUrl = chapterDetails
		? `${constants.ApiUrl}/get-podcast-chunk/${getCookie(COOKIE.UserId)}/${chapterDetails?.resource_file_id}/index.m3u8`
		: null;

	// Attach HLS.js to the audio tag
	useEffect(() => {
		if (!hlsUrl) return;
		const audio = audioRef.current?.audio?.current;
		if (!audio) return;
		setIsLoading(true);
		setAudioLoading(true);
		let hls;
		const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
		const isSafari =  /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
		const useNativeHls = isIOS || isSafari;
		if (useNativeHls) {
			audio.src = hlsUrl;
			setIsLoading(false);
			setAudioLoading(false);
		}
		else if (Hls.isSupported()) {
			hls = new Hls({
				xhrSetup: function (xhr, url) {
					xhr.setRequestHeader('Authorization', `Bearer ${getCookie(COOKIE.Token)}`);
				}
			});
			hls.loadSource(hlsUrl);
			hls.attachMedia(audio);
			hls.on(Hls.Events.MANIFEST_PARSED, () => {
				setIsLoading(false);
				setAudioLoading(false);
				if (isPlaying) {
					audio.play().catch(err => console.error("Play error:", err));
				}
				if (!Number.isFinite(audio.volume)) {
					audio.volume = 0.5;
				}
			});
			hls.on(Hls.Events.ERROR, (event, data) => {
				console.error("HLS.js error", data);
			});

		}else {
			console.error("HLS is not supported in this browser.");
			setIsLoading(false);
			setAudioLoading(false);
		}

		return () => {
			if (hls) {
            	hls.destroy();
        	}
		};
	}, [hlsUrl]);

	useEffect(() => {
		const audio = audioRef.current?.audio?.current;
		if (!audio || isLoading) return;
		if (isPlaying) {
			audio.play().catch(err => {
				console.error("Playback error:", err);
			});
		} else {
			audio.pause();
		}
	}, [isPlaying, isLoading]);

	useEffect(() => {
		if (chapterDetails) {
			const el = document.getElementById(`podcast-${chapterDetails.chapter_id}`);
			el?.scrollIntoView({ behavior: "smooth", block: "center" });
		}
	}, [chapterDetails]);

	const getPlayedAudio = () => {
		if (!isPodcastPlayed) {
			fetch(
				`${constants.ApiUrl}/update-played-podcast?id=${chapter_id}&stud_id=${getCookie(COOKIE.UserId)}`,
				{ method: "GET" }
			).then(async res => {
				const data = await res.json();
				if (data?.code === 200) {
					setIsPodcastPlayed(true);
				} else {
					console.error("API failed");
				}
			});
		}
	};

	return (
		<div className="relative w-full">
			{isLoading && (
				<div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10">
					<Spin tip="Loading Audio..." size="large" />
				</div>
			)}
			
				<AudioPlayer
					layout="horizontal-reverse"
					className="border-black border rounded-none p-1"
					style={{
						borderColor: !chapterDetails?.from_chapter_resource
							? getColorClass(chapter_id, "colorcode")
							: "",
					}}
					ref={audioRef}
					preload="metadata"
					showJumpControls
					showFilledProgress
					showFilledVolume
					autoPlay={false}
					onPlay={() => {
						setIsPlaying(true);
						getPlayedAudio();
					}}
					onPause={() => {
						setIsPlaying(false);
					}}
					progressJumpSteps={{
						forward: 30000,
						backward: 10000
					}}
					header={
						<div
							style={{ color: getColorClass(chapter_id, "colorcode") }}
							className="text-2xl text-center"
						>
							{!chapterDetails?.from_chapter_resource
								? `Chapter ${chapter_id} ${chapterDetails?.chapter_name}`
								: ""}
						</div>
					}
					customAdditionalControls={["jump-back", "jump-forward", "volume"]}
					customControlsSection={[
						RHAP_UI.MAIN_CONTROLS,
						RHAP_UI.VOLUME_CONTROLS,
					]}
					hasDefaultKeyBindings={false}
				/>
		</div>
	);
};

export default AudioComponent;
